/**
 * **Daily uploads, approvals and pending** (W22, FR-57, plan D54) — the home
 * screen's chart: the last 14 IST days, oldest first, three columns a day.
 *
 * - **Uploaded** — documents first stored that day;
 * - **Approved** — documents approved that day, or whose extraction was
 *   accepted that day (by a reviewer or automatically), once each;
 * - **Pending** — of that day's uploads, those still waiting for a review now.
 *
 * Every number is the summary's (`daily`); the chart computes nothing but
 * geometry. **The colours are the kit's icon triad** — `--tx-blue`,
 * `--tx-purple`, `--tx-orange` (ADR-0089 §2): each clears 3:1 on the page's
 * surface and the three stay apart for colour-blind readers (checked with the
 * dataviz validator: worst adjacent pair ΔE 14.4 deutan, 19.4 normal vision).
 * Colour is never the only signal: a legend names each series, every day's
 * three numbers come on hover, tap and keyboard focus, and **Show as table**
 * carries the same numbers in rows.
 *
 * The columns are SVG, one small drawing per day in a grid, so a phone's
 * narrow card squeezes the gaps rather than the type; the labels and the
 * gridlines are HTML around them. A day with nothing is still a column — its
 * numbers are zeros, and it says so.
 */
import { DataTable, cn, formatDate, formatDayMonth, formatNumber, type ColumnDef } from '@fiveox/ui'
import { useId, useState, type KeyboardEvent } from 'react'

/** One IST day — the summary's `daily` entry. */
export interface DailyPoint {
  /** `YYYY-MM-DD`, India time. */
  date: string
  uploaded: number
  approved: number
  pending: number
}

type SeriesKey = 'uploaded' | 'approved' | 'pending'

/** In the order the columns stand each day, and the legend reads. */
export const SERIES: readonly { key: SeriesKey; label: string; color: string }[] = [
  { key: 'uploaded', label: 'Uploaded', color: 'var(--tx-blue)' },
  { key: 'approved', label: 'Approved', color: 'var(--tx-purple)' },
  { key: 'pending', label: 'Pending', color: 'var(--tx-orange)' },
]

/* The drawing of one day: three 10px columns with a 2px gap between them. */
const BAR = 10
const GAP = 2
const WIDTH = SERIES.length * BAR + (SERIES.length - 1) * GAP
const HEIGHT = 160
/** The rounded data end; the baseline end stays square. */
const RADIUS = 4

/** A clean top for the scale: 1, 2 or 5 times a power of ten, at least the largest value. */
export function niceMax(max: number): number {
  if (max <= 0) return 4
  const power = 10 ** Math.floor(Math.log10(max))
  for (const step of [1, 2, 5, 10]) if (step * power >= max) return step * power
  return 10 * power
}

/** The ticks a scale up to `top` shows: zero, the middle and the top — whole numbers only. */
export function ticksOf(top: number): number[] {
  return top % 2 === 0 ? [0, top / 2, top] : [0, top]
}

/** A column from the baseline, its top corners rounded. */
function columnPath(x: number, height: number): string {
  const y = HEIGHT - height
  const r = Math.min(RADIUS, height, BAR / 2)
  return [
    `M${x},${HEIGHT}`,
    `V${y + r}`,
    `Q${x},${y} ${x + r},${y}`,
    `H${x + BAR - r}`,
    `Q${x + BAR},${y} ${x + BAR},${y + r}`,
    `V${HEIGHT}`,
    'Z',
  ].join(' ')
}

/** `2026-10-15` → `15 Oct`, read as the calendar day it names (never shifted by the browser's zone). */
function shortDay(date: string): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number]
  return formatDayMonth(new Date(y, m - 1, d))
}

/** A day's three numbers, as a sentence — the column's accessible name and the tooltip's content. */
export function daySentence(day: DailyPoint): string {
  return `${formatDate(day.date)}: ${SERIES.map((s) => `${formatNumber(day[s.key])} ${s.label.toLowerCase()}`).join(', ')}`
}

export function DailyChart({ days }: { days: DailyPoint[] }) {
  const baseId = useId()
  // The day whose numbers show — hovered, tapped or focused. `null`: none.
  const [active, setActive] = useState<number | null>(null)
  // Which day the keyboard enters on: the last one touched, else today.
  const [focusable, setFocusable] = useState<number>(Math.max(days.length - 1, 0))
  const top = niceMax(Math.max(0, ...days.flatMap((d) => SERIES.map((s) => d[s.key]))))
  const ticks = ticksOf(top)
  const dayId = (i: number) => `${baseId}-day-${i}`

  const move = (e: KeyboardEvent<HTMLLIElement>, i: number) => {
    const to = e.key === 'ArrowRight' ? i + 1 : e.key === 'ArrowLeft' ? i - 1 : e.key === 'Home' ? 0 : e.key === 'End' ? days.length - 1 : null
    if (e.key === 'Escape') {
      setActive(null)
      return
    }
    if (to === null || to < 0 || to >= days.length) return
    e.preventDefault()
    setFocusable(to)
    document.getElementById(dayId(to))?.focus()
  }

  return (
    <figure className="space-y-4">
      <ul className="flex flex-wrap gap-x-5 gap-y-1" aria-label="Legend">
        {SERIES.map((s) => (
          <li key={s.key} className="text-muted-foreground flex items-center gap-2 text-xs">
            <svg width="10" height="10" aria-hidden className="shrink-0">
              <rect width="10" height="10" rx="2" style={{ fill: s.color }} />
            </svg>
            {s.label}
          </li>
        ))}
      </ul>

      <div className="flex gap-2">
        {/* The scale: zero, the middle and the top, beside their gridlines. */}
        <div className="relative h-40 w-7 shrink-0" aria-hidden>
          {ticks.map((t) => (
            <span key={t} className="text-muted-foreground absolute right-0 translate-y-1/2 text-[11px] leading-none" style={{ bottom: `${(t / top) * 100}%` }}>
              {formatNumber(t)}
            </span>
          ))}
        </div>

        <div className="min-w-0 flex-1">
          <div className="relative h-40">
            {ticks.map((t) => (
              <span key={t} aria-hidden className="border-subtle absolute inset-x-0 border-t" style={{ bottom: `${(t / top) * 100}%` }} />
            ))}
            <ol
              className="relative grid h-full"
              style={{ gridTemplateColumns: `repeat(${Math.max(days.length, 1)}, minmax(0, 1fr))` }}
              aria-label="Uploads, approvals and pending, by day"
              onPointerLeave={(e) => e.pointerType === 'mouse' && setActive(null)}
            >
              {days.map((day, i) => {
                const shown = active === i
                return (
                  <li
                    key={day.date}
                    id={dayId(i)}
                    tabIndex={i === focusable ? 0 : -1}
                    aria-label={daySentence(day)}
                    aria-describedby={shown ? `${baseId}-tip` : undefined}
                    data-date={day.date}
                    className={cn('focus-visible:ring-ring relative flex h-full cursor-default justify-center rounded-md outline-none focus-visible:ring-2', shown && 'bg-muted/60')}
                    onPointerEnter={() => setActive(i)}
                    // A tap: the day stays shown until another is tapped or the focus leaves the chart.
                    onClick={() => setActive(i)}
                    onFocus={() => {
                      setActive(i)
                      setFocusable(i)
                    }}
                    onBlur={(e) => {
                      if (!e.currentTarget.parentElement?.contains(e.relatedTarget as Node | null)) setActive(null)
                    }}
                    onKeyDown={(e) => move(e, i)}
                  >
                    <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} preserveAspectRatio="none" className="h-full w-full max-w-[34px]" aria-hidden>
                      {SERIES.map((s, k) => {
                        const value = day[s.key]
                        if (value <= 0) return null
                        // A non-zero count is never drawn thinner than a sliver.
                        const height = Math.max((value / top) * HEIGHT, 2)
                        return <path key={s.key} d={columnPath(k * (BAR + GAP), height)} style={{ fill: s.color }} data-series={s.key} />
                      })}
                    </svg>
                    {shown && <DayTip id={`${baseId}-tip`} day={day} align={i < 3 ? 'start' : i > days.length - 4 ? 'end' : 'center'} />}
                  </li>
                )
              })}
            </ol>
          </div>
          <ol className="mt-1.5 grid" style={{ gridTemplateColumns: `repeat(${Math.max(days.length, 1)}, minmax(0, 1fr))` }} aria-hidden>
            {days.map((day, i) => (
              // Below `sm` every other day is named, counting back from today, so the labels never collide.
              <li key={day.date} className={cn('text-muted-foreground truncate text-center text-[11px]', (days.length - 1 - i) % 2 === 1 && 'hidden sm:block')}>
                {shortDay(day.date)}
              </li>
            ))}
          </ol>
        </div>
      </div>

      <details className="group">
        <summary className="text-primary w-fit cursor-pointer text-sm font-medium underline-offset-2 hover:underline">Show as table</summary>
        <div className="mt-3">
          <DataTable columns={TABLE_COLUMNS} rows={[...days].reverse()} rowKey={(d) => d.date} />
        </div>
      </details>
    </figure>
  )
}

/** The newest day first — the table is read, not scanned along an axis. */
const TABLE_COLUMNS: ColumnDef<DailyPoint>[] = [
  { key: 'date', label: 'Day', width: 140, render: (d) => formatDate(d.date) },
  ...SERIES.map((s): ColumnDef<DailyPoint> => ({ key: s.key, label: s.label, width: 120, align: 'right', render: (d) => formatNumber(d[s.key]) })),
]

/** A day's three numbers: the value leads, the series follows, keyed by a short stroke of its colour. */
function DayTip({ id, day, align }: { id: string; day: DailyPoint; align: 'start' | 'center' | 'end' }) {
  return (
    <span
      id={id}
      role="tooltip"
      // `on-dark` re-points the triad at its dark-surface values, so each key clears 3:1 on the tooltip too.
      className={cn(
        'on-dark bg-foreground text-background pointer-events-none absolute bottom-full z-20 mb-2 w-max rounded-md px-2.5 py-2 text-xs shadow-lg',
        align === 'start' ? 'left-0' : align === 'end' ? 'right-0' : 'left-1/2 -translate-x-1/2',
      )}
    >
      <span className="block font-medium">{formatDate(day.date)}</span>
      {SERIES.map((s) => (
        <span key={s.key} className="mt-1 flex items-center gap-2">
          <svg width="12" height="2" aria-hidden className="shrink-0">
            <rect width="12" height="2" rx="1" style={{ fill: s.color }} />
          </svg>
          <b className="font-semibold tabular-nums">{formatNumber(day[s.key])}</b>
          <span className="opacity-80">{s.label}</span>
        </span>
      ))}
    </span>
  )
}
